<script setup>
import { ref, watch } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';
import {
  formatDate,
  TYPE_LABELS,
  TYPE_COLORS,
} from './useMonitorHelpers';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
});

const isTrackerLoading = ref(false);
const trackerData = ref(null);
const trackerPrompt = ref('');
const isAiLoading = ref(false);

async function fetchTracker() {
  if (!props.selectedInbox) return;
  isTrackerLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.tracker({
      inboxId: props.selectedInbox.id,
    });
    trackerData.value = response.data;
  } catch {
    // silently fail
  } finally {
    isTrackerLoading.value = false;
  }
}

async function askAi() {
  if (!props.selectedInbox || !trackerPrompt.value.trim()) return;
  isAiLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.tracker({
      inboxId: props.selectedInbox.id,
      prompt: trackerPrompt.value.trim(),
    });
    trackerData.value = response.data;
  } catch {
    // silently fail
  } finally {
    isAiLoading.value = false;
  }
}

watch(
  () => props.selectedInbox,
  inbox => {
    if (inbox) fetchTracker();
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <!-- AI Prompt -->
    <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
      <h3 class="text-lg font-semibold mb-3 text-n-slate-12">
        {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_AI_TITLE') }}
      </h3>
      <p class="text-xs text-n-slate-11 mb-4">
        {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_AI_DESC') }}
      </p>
      <div class="flex gap-3">
        <textarea
          v-model="trackerPrompt"
          class="flex-1 py-2 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12 resize-none"
          rows="3"
          :placeholder="
            $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_AI_PLACEHOLDER')
          "
          @keydown.ctrl.enter="askAi"
        />
        <button
          class="self-end px-4 py-2 text-sm font-medium rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-colors disabled:opacity-50"
          :disabled="isAiLoading || !trackerPrompt.trim() || !selectedInbox"
          @click="askAi"
        >
          <Spinner v-if="isAiLoading" size="small" />
          <span v-else>{{
            $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_AI_BUTTON')
          }}</span>
        </button>
      </div>
    </div>

    <!-- Loading tracker -->
    <div
      v-if="isTrackerLoading"
      class="flex items-center justify-center py-12"
    >
      <Spinner />
    </div>

    <template v-else-if="trackerData">
      <!-- Profile Card -->
      <div
        v-if="trackerData.profile && trackerData.profile.username"
        class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6"
      >
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
          {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_PROFILE') }}
        </h3>
        <div class="flex items-start gap-4">
          <img
            v-if="trackerData.profile.profile_picture_url"
            :src="trackerData.profile.profile_picture_url"
            class="w-16 h-16 rounded-full object-cover flex-shrink-0"
            loading="lazy"
          />
          <div class="flex-1">
            <p class="text-lg font-semibold text-n-slate-12">
              {{ trackerData.profile.name }}
            </p>
            <p class="text-sm text-n-slate-11 mb-2">
              {{
                $t('INSTAGRAM_MEDIA_REPORTS.USERNAME_PREFIX', {
                  username: trackerData.profile.username,
                })
              }}
            </p>
            <p
              v-if="trackerData.profile.biography"
              class="text-sm text-n-slate-12 mb-3"
            >
              {{ trackerData.profile.biography }}
            </p>
            <div class="flex gap-6">
              <div class="text-center">
                <p class="text-lg font-semibold text-n-slate-12">
                  {{
                    (
                      trackerData.profile.followers_count || 0
                    ).toLocaleString()
                  }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.FOLLOWERS') }}
                </p>
              </div>
              <div class="text-center">
                <p class="text-lg font-semibold text-n-slate-12">
                  {{
                    (
                      trackerData.profile.follows_count || 0
                    ).toLocaleString()
                  }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.FOLLOWING') }}
                </p>
              </div>
              <div class="text-center">
                <p class="text-lg font-semibold text-n-slate-12">
                  {{
                    (trackerData.profile.media_count || 0).toLocaleString()
                  }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ $t('INSTAGRAM_MEDIA_REPORTS.CHART_POSTS') }}
                </p>
              </div>
            </div>
            <a
              v-if="trackerData.profile.website"
              :href="trackerData.profile.website"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm text-n-blue-11 hover:text-n-blue-12 mt-2 inline-block"
            >
              {{ trackerData.profile.website }}
            </a>
          </div>
        </div>
      </div>

      <!-- AI Analysis -->
      <div
        v-if="trackerData.ai_analysis"
        class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6"
      >
        <h3 class="text-lg font-semibold mb-3 text-n-slate-12">
          {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_AI_RESULT') }}
        </h3>
        <div
          v-if="trackerData.ai_analysis.error"
          class="text-sm text-n-ruby-11 bg-n-ruby-3 p-3 rounded-lg"
        >
          {{ trackerData.ai_analysis.error }}
        </div>
        <div
          v-else
          class="text-sm text-n-slate-12 whitespace-pre-wrap leading-relaxed"
        >
          {{ trackerData.ai_analysis.content }}
        </div>
      </div>

      <!-- Tagged Media -->
      <div
        v-if="trackerData.tagged_media && trackerData.tagged_media.length"
        class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6"
      >
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">
          {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_TAGGED') }}
        </h3>
        <div class="space-y-3">
          <div
            v-for="(post, idx) in trackerData.tagged_media"
            :key="idx"
            class="flex items-center gap-4 p-3 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors"
          >
            <span class="text-lg font-bold text-n-slate-9 w-8 text-center">
              {{ idx + 1 }}
            </span>
            <div class="flex-1 min-w-0">
              <p class="text-sm text-n-slate-12 truncate">
                {{
                  post.caption || $t('INSTAGRAM_MEDIA_REPORTS.NO_CAPTION')
                }}
              </p>
              <div class="flex gap-3 mt-1">
                <span
                  class="text-xs px-2 py-0.5 rounded-full text-white"
                  :class="TYPE_COLORS[post.media_type] || 'bg-n-blue-3'"
                >
                  {{ TYPE_LABELS[post.media_type] || post.media_type }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{ post.timestamp ? formatDate(post.timestamp) : '' }}
                </span>
              </div>
            </div>
            <div class="flex gap-4 text-sm flex-shrink-0">
              <span class="text-n-pink-11 font-medium">
                {{
                  $t('INSTAGRAM_MEDIA_REPORTS.LIKES_LABEL', {
                    count: post.like_count,
                  })
                }}
              </span>
              <span class="text-n-blue-11 font-medium">
                {{
                  $t('INSTAGRAM_MEDIA_REPORTS.COMMENTS_LABEL', {
                    count: post.comments_count,
                  })
                }}
              </span>
            </div>
            <a
              v-if="post.permalink"
              :href="post.permalink"
              target="_blank"
              rel="noopener noreferrer"
              class="text-n-blue-11 hover:text-n-blue-12 text-sm flex-shrink-0"
            >
              {{ $t('INSTAGRAM_MEDIA_REPORTS.OPEN_LINK') }}
            </a>
          </div>
        </div>
      </div>
    </template>

    <!-- No inbox selected -->
    <div
      v-else-if="!selectedInbox"
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_SELECT_INBOX') }}
    </div>
  </div>
</template>
